import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import TextField from 'material-ui/TextField';
import { withTranslation } from 'react-i18next';

import CollapsibleField from '../CollapsibleField/CollapsibleField';
import './style.css';
import {filteringFieldsForRecordListView, filteringFieldsForRecordListViewOtherFields} from "../../../config/constants";

// Heights in px
var SECTION_HEADER_HEIGHT = 31;
var FIELD_ROW_HEIGHT = 38;
var OVERSCAN = 15;

// Section header suuids (includes both rules and column picker headers)
var SECTION_HEADER_SUUIDS = ['Entry_Details', 'Entry_Fields', 'Other_Fields', 'Fields_For_Record'];

function isSectionHeaderField(f) {
  return f.disable && (f.isSectionHeader || SECTION_HEADER_SUUIDS.indexOf(f.suuid) !== -1);
}

// --- Pure helper functions (no hooks, no state) ---

// Collect all group suuids from a field tree
var collectGroupSuuids = function(fields, result) {
  if (!fields) return;
  for (var i = 0; i < fields.length; i++) {
    var field = fields[i];
    if (field.children && field.children.length > 0) {
      result.push(field.suuid);
      collectGroupSuuids(field.children, result);
    }
  }
};

// Compute visible suuids for search: returns a Set of suuids that match or have matching descendants
var computeVisibleSuuids = function(fields, searchText) {
  if (!searchText) return null; // null means "show all"
  var visible = {};

  var walk = function(field) {
    var titleMatch = field.title && field.title.toLowerCase().indexOf(searchText) !== -1;
    var typeMatch = field.type && field.type.toLowerCase().indexOf(searchText) !== -1;
    var selfMatches = titleMatch || typeMatch;
    var childMatches = false;

    if (field.children) {
      for (var i = 0; i < field.children.length; i++) {
        if (walk(field.children[i])) {
          childMatches = true;
        }
      }
    }

    if (selfMatches || childMatches) {
      visible[field.suuid] = true;
      // If self matches and has children, mark all descendants visible too
      if (selfMatches && field.children) {
        markAllDescendants(field.children, visible);
      }
      return true;
    }
    return false;
  };

  var markAllDescendants = function(children, vis) {
    for (var i = 0; i < children.length; i++) {
      vis[children[i].suuid] = true;
      if (children[i].children) {
        markAllDescendants(children[i].children, vis);
      }
    }
  };

  for (var i = 0; i < fields.length; i++) {
    walk(fields[i]);
  }

  // Convert to Set
  var set = new Set();
  var keys = Object.keys(visible);
  for (var j = 0; j < keys.length; j++) {
    set.add(keys[j]);
  }
  return set;
};

// Count visible children recursively for height calculation
var countVisibleChildren = function(field, expandedGroups, visibleSuuids, recordsFieldId) {
  if (!field.children || !expandedGroups.has(field.suuid)) return 0;
  var count = 0;
  for (var i = 0; i < field.children.length; i++) {
    var child = field.children[i];
    // Skip records field (hidden by CollapsibleField)
    if (recordsFieldId && child.suuid === recordsFieldId) continue;
    // Skip if search is active and child is not visible
    if (visibleSuuids && !visibleSuuids.has(child.suuid)) continue;
    count += 1;
    if (child.children && child.children.length > 0) {
      count += countVisibleChildren(child, expandedGroups, visibleSuuids, recordsFieldId);
    }
  }
  return count;
};

// Binary search: find the index of the first item whose cumulative bottom >= target
var binarySearchCumulative = function(cumHeights, target) {
  var lo = 0;
  var hi = cumHeights.length - 1;
  while (lo < hi) {
    var mid = (lo + hi) >>> 1;
    if (cumHeights[mid] < target) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
};


var RuleFields = function(props) {
  var fields = props.fields;
  var onFieldClick = props.onFieldClick;
  var onDragStart = props.onDragStart;
  var isRecordListView = props.isRecordListView;
  var allFields = props.allFields;
  var selectedFields = props.selectedFields;
  var recordsFieldUniquenessIdentifier = props.recordsFieldUniquenessIdentifier;
  var isColumnPicker = props.isColumnPicker;
  var recordFieldTitle = props.recordFieldTitle;
  var onBulkAdd = props.onBulkAdd;
  var useDoubleClick = props.useDoubleClick;
  var searchPlaceholder = props.searchPlaceholder;
  var hideSearch = props.hideSearch;
  var externalSearchQuery = props.externalSearchQuery;
  var showAddButton = props.showAddButton;
  var hideEntryDetails = props.hideEntryDetails;
  var hideOtherFields = props.hideOtherFields;
  var t = props.t;

  var _searchQuery = useState('');
  var searchQuery = _searchQuery[0];
  var setSearchQuery = _searchQuery[1];

  // Use external search query when provided (controlled mode), otherwise use internal state
  var effectiveQuery = typeof externalSearchQuery === 'string' ? externalSearchQuery : searchQuery;

  // Visible range state -- only updated when items enter/leave the viewport
  var _visibleRange = useState({ startIndex: 0, endIndex: -1, topSpacer: 0, bottomSpacer: 0 });
  var visibleRange = _visibleRange[0];
  var setVisibleRange = _visibleRange[1];

  var _containerHeight = useState(400);
  var containerHeight = _containerHeight[0];
  var setContainerHeight = _containerHeight[1];

  var scrollRef = useRef(null);
  var scrollTopRef = useRef(0);
  var rafIdRef = useRef(null);
  // Refs to let the rAF scroll handler access current data without closure staleness
  var topLevelItemsRef = useRef([]);
  var cumHeightsRef = useRef([]);
  var containerHeightRef = useRef(400);
  // Refs for stable handleBulkAdd (avoids re-creating on every render, preserves memo on CollapsibleField)
  var onBulkAddRef = useRef(onBulkAdd);
  var visibleSuuidsRef = useRef(null);
  var effectiveQueryRef = useRef('');
  var _stableBulkAdd = useRef(function(field) {
    onBulkAddRef.current(field, visibleSuuidsRef.current, effectiveQueryRef.current);
  }).current;

  // Disable drag when no onDragStart handler is provided (selection-only mode)
  var disableDrag = !onDragStart;

  var handleBulkAdd = onBulkAdd ? _stableBulkAdd : undefined;

  // Helper to deduplicate fields by suuid, keeping the first occurrence
  var deduplicateFields = function(fieldList) {
    if (!fieldList) return [];
    var seen = {};
    return fieldList.filter(function(field) {
      if (!field || !field.suuid) return true;
      if (seen[field.suuid]) return false;
      seen[field.suuid] = true;
      return true;
    });
  };

  // Helper to mark isShown and alreadySelected on fields
  var markFieldsShownAndSelected = function(fieldList, selected, clearParent) {
    if (!fieldList) return [];
    selected = selected || [];
    return fieldList.map(function(field) {
      var isAlreadySelected = selected.findIndex(function(sel) {
        return sel.suuid === field.suuid || sel.fieldSUUID === field.suuid;
      }) !== -1;
      var marked = Object.assign({}, field, { isShown: true });
      if (clearParent) {
        marked.parent = undefined;
      }
      if (isAlreadySelected) {
        marked.disable = true;
        marked.alreadySelected = true;
      }
      if (marked.children && marked.children.length > 0) {
        marked.children = markFieldsShownAndSelected(marked.children, selected, false);
      }
      return marked;
    });
  };

  var availableItemsList = function() {
    if (isRecordListView) {
      if (isColumnPicker) {
        var recordField = allFields && allFields.find(function(field) {
          return field.suuid === recordsFieldUniquenessIdentifier;
        });
        var recordChildren = recordField && recordField.children ? recordField.children : [];
        var markedRecordChildren = markFieldsShownAndSelected(recordChildren, selectedFields, true);

        var recordChildrenSuuids = [];
        var collectChildSuuids = function(children) {
          if (!children) return;
          children.forEach(function(child) {
            recordChildrenSuuids.push(child.suuid);
            if (child.children) {
              collectChildSuuids(child.children);
            }
          });
        };
        collectChildSuuids(recordChildren);

        var otherFields = allFields ? allFields.filter(function(field) {
          return field.suuid !== recordsFieldUniquenessIdentifier &&
                 recordChildrenSuuids.indexOf(field.suuid) === -1;
        }) : [];
        var markedOtherFields = markFieldsShownAndSelected(otherFields, selectedFields, true);

        return deduplicateFields([
          {
            isShown: true,
            suuid: 'Fields_For_Record',
            title: t('settings:fieldsFor'),
            titleBold: recordFieldTitle || '',
            type: '',
            disable: true,
          },
          ...markedRecordChildren,
          {
            isShown: true,
            suuid: 'Other_Fields',
            title: t('settings:otherFields'),
            type: '',
            disable: true,
          },
          ...markedOtherFields,
        ]);
      }

      var safeFields = fields || [];
      var availableWithoutRecordsFields = allFields ? allFields.filter(function(field) {
        return (safeFields.findIndex(function(_field) {
          return _field.suuid === field.suuid;
        }) === -1) && (field.suuid !== recordsFieldUniquenessIdentifier);
      }) : [];

      // When Entry Details is hidden, keep only the "Entry Fields" header
      // (the last item in filteringFieldsForRecordListView) so the section
      // label still appears before the record's own fields.
      var leadingSectionItems = hideEntryDetails
        ? [filteringFieldsForRecordListView[filteringFieldsForRecordListView.length - 1]]
        : filteringFieldsForRecordListView;
      var trailingSectionItems = hideOtherFields
        ? []
        : filteringFieldsForRecordListViewOtherFields.concat(availableWithoutRecordsFields);

      return deduplicateFields([
        ...leadingSectionItems,
        ...safeFields,
        ...trailingSectionItems,
      ]);
    }
    return fields;
  };

  // Memoize the base field list
  var availableLocalList = useMemo(function() {
    return availableItemsList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, isRecordListView, selectedFields, allFields, recordsFieldUniquenessIdentifier, isColumnPicker, recordFieldTitle, hideEntryDetails, hideOtherFields]);

  // Initialize expandedGroups with all group suuids expanded
  var _expandedGroups = useState(function() { return new Set(); });
  var expandedGroups = _expandedGroups[0];
  var setExpandedGroups = _expandedGroups[1];

  // When the field list changes, ensure all groups are in expandedGroups
  useEffect(function() {
    var groupSuuids = [];
    collectGroupSuuids(availableLocalList, groupSuuids);
    if (groupSuuids.length > 0) {
      setExpandedGroups(function(prev) {
        // On first load (empty set), expand all. On subsequent updates, add new groups.
        if (prev.size === 0) {
          return new Set(groupSuuids);
        }
        var next = new Set(prev);
        var changed = false;
        for (var i = 0; i < groupSuuids.length; i++) {
          if (!next.has(groupSuuids[i])) {
            next.add(groupSuuids[i]);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [availableLocalList, setExpandedGroups]);

  var handleToggleGroup = useCallback(function(suuid) {
    setExpandedGroups(function(prev) {
      var next = new Set(prev);
      if (next.has(suuid)) {
        next.delete(suuid);
      } else {
        next.add(suuid);
      }
      return next;
    });
  }, [setExpandedGroups]);

  // Compute visible suuids for search
  var searchText = effectiveQuery.trim().toLowerCase();
  var visibleSuuids = useMemo(function() {
    return computeVisibleSuuids(availableLocalList, searchText);
  }, [availableLocalList, searchText]);

  // Build flat top-level items list with precomputed heights
  // Each item: { type: 'header'|'field', field, height, isFirst, sectionIndex, isLast }
  var topLevelItems = useMemo(function() {
    var items = [];

    if (isRecordListView || availableLocalList.some(function(f) { return f.isSectionHeader; })) {
      var currentSectionIndex = -1;
      var isFirstHeader = true;

      // First pass: collect top-level items (skip parent fields and records field)
      var topFields = [];
      for (var i = 0; i < availableLocalList.length; i++) {
        var field = availableLocalList[i];
        if (field.parent || field.suuid === recordsFieldUniquenessIdentifier) continue;
        topFields.push(field);
      }

      // Build items with section tracking for isLast per section
      var sectionLastMap = {}; // sectionIndex -> last visible field index in that section
      currentSectionIndex = -1;
      for (var m = 0; m < topFields.length; m++) {
        var f = topFields[m];
        var isH = isSectionHeaderField(f);
        if (isH) {
          currentSectionIndex++;
        } else {
          if (visibleSuuids && !visibleSuuids.has(f.suuid)) continue;
          sectionLastMap[currentSectionIndex] = m;
        }
      }

      currentSectionIndex = -1;
      for (var j = 0; j < topFields.length; j++) {
        var field2 = topFields[j];
        var isSH = isSectionHeaderField(field2);

        if (isSH) {
          currentSectionIndex++;
          // Check if this section has any visible fields
          var sectionHasVisible = sectionLastMap[currentSectionIndex] !== undefined;
          if (!sectionHasVisible && visibleSuuids) continue; // Skip empty sections during search

          items.push({
            type: 'header',
            field: field2,
            height: SECTION_HEADER_HEIGHT,
            isFirst: isFirstHeader,
            sectionIndex: currentSectionIndex,
          });
          isFirstHeader = false;
        } else {
          if (visibleSuuids && !visibleSuuids.has(field2.suuid)) continue;
          var childCount = countVisibleChildren(field2, expandedGroups, visibleSuuids, recordsFieldUniquenessIdentifier);
          var height = FIELD_ROW_HEIGHT * (1 + childCount);
          var isLastInSection = sectionLastMap[currentSectionIndex] === j;
          items.push({
            type: 'field',
            field: field2,
            height: height,
            sectionIndex: currentSectionIndex,
            isLast: isLastInSection,
          });
        }
      }
    } else {
      // Non-record list view: group by meta
      var metaFields = [];
      var regularFields = [];
      for (var n = 0; n < availableLocalList.length; n++) {
        var f2 = availableLocalList[n];
        if (f2.parent) continue;
        if (visibleSuuids && !visibleSuuids.has(f2.suuid)) continue;
        if (f2.meta) {
          metaFields.push(f2);
        } else {
          regularFields.push(f2);
        }
      }

      if (metaFields.length > 0) {
        items.push({
          type: 'header',
          field: { suuid: '_meta_header', title: t('settings:slateDetails'), type: '', disable: true },
          height: SECTION_HEADER_HEIGHT,
          isFirst: true,
          sectionIndex: 0,
        });
        for (var p = 0; p < metaFields.length; p++) {
          var childCount2 = countVisibleChildren(metaFields[p], expandedGroups, visibleSuuids, recordsFieldUniquenessIdentifier);
          items.push({
            type: 'field',
            field: metaFields[p],
            height: FIELD_ROW_HEIGHT * (1 + childCount2),
            sectionIndex: 0,
            isLast: p === metaFields.length - 1,
          });
        }
      }

      if (regularFields.length > 0) {
        items.push({
          type: 'header',
          field: { suuid: '_regular_header', title: t('settings:slateFields'), type: '', disable: true },
          height: SECTION_HEADER_HEIGHT,
          isFirst: metaFields.length === 0,
          sectionIndex: 1,
        });
        for (var q = 0; q < regularFields.length; q++) {
          var childCount3 = countVisibleChildren(regularFields[q], expandedGroups, visibleSuuids, recordsFieldUniquenessIdentifier);
          items.push({
            type: 'field',
            field: regularFields[q],
            height: FIELD_ROW_HEIGHT * (1 + childCount3),
            sectionIndex: 1,
            isLast: q === regularFields.length - 1,
          });
        }
      }
    }

    // Attach sectionFields to headers for section-level bulk add
    var sectionFieldsMap = {};
    for (var sf = 0; sf < items.length; sf++) {
      if (items[sf].type === 'field') {
        var secIdx = items[sf].sectionIndex;
        if (!sectionFieldsMap[secIdx]) sectionFieldsMap[secIdx] = [];
        sectionFieldsMap[secIdx].push(items[sf].field);
      }
    }
    for (var hf = 0; hf < items.length; hf++) {
      if (items[hf].type === 'header') {
        items[hf].sectionFields = sectionFieldsMap[items[hf].sectionIndex] || [];
      }
    }

    return items;
  }, [availableLocalList, isRecordListView, expandedGroups, visibleSuuids, recordsFieldUniquenessIdentifier, t]);

  // Cumulative heights for binary search
  var cumHeights = useMemo(function() {
    var arr = new Array(topLevelItems.length);
    var sum = 0;
    for (var i = 0; i < topLevelItems.length; i++) {
      sum += topLevelItems[i].height;
      arr[i] = sum;
    }
    return arr;
  }, [topLevelItems]);

  // Keep refs in sync so the rAF scroll handler always reads current data
  topLevelItemsRef.current = topLevelItems;
  cumHeightsRef.current = cumHeights;
  containerHeightRef.current = containerHeight;
  onBulkAddRef.current = onBulkAdd;
  visibleSuuidsRef.current = visibleSuuids;
  effectiveQueryRef.current = effectiveQuery;

  // Helper: compute visible range from a scrollTop value (uses refs)
  var computeRange = function(st) {
    var items = topLevelItemsRef.current;
    var cum = cumHeightsRef.current;
    var ch = containerHeightRef.current;
    var si, ei;

    if (items.length === 0) {
      return { startIndex: 0, endIndex: -1, topSpacer: 0, bottomSpacer: 0 };
    }

    si = binarySearchCumulative(cum, st);
    ei = binarySearchCumulative(cum, st + ch);

    // Apply overscan
    si = Math.max(0, si - OVERSCAN);
    ei = Math.min(items.length - 1, ei + OVERSCAN);

    var total = cum.length > 0 ? cum[cum.length - 1] : 0;
    var topSp = si > 0 ? cum[si - 1] : 0;
    var bottomSp = ei < items.length - 1 ? total - cum[ei] : 0;

    return { startIndex: si, endIndex: ei, topSpacer: topSp, bottomSpacer: bottomSp };
  };

  // When topLevelItems or cumHeights change (expand/collapse, search, etc.), recompute range immediately
  useEffect(function() {
    var range = computeRange(scrollTopRef.current);
    setVisibleRange(range);
  }, [topLevelItems, cumHeights, containerHeight, setVisibleRange]);

  // Read range values for rendering (clamp to current list length in case state is stale)
  var startIndex = visibleRange.startIndex;
  var endIndex = Math.min(visibleRange.endIndex, topLevelItems.length - 1);
  var topSpacerHeight = visibleRange.topSpacer;
  var bottomSpacerHeight = visibleRange.bottomSpacer;

  // When the current section's header is above the visible range, render it separately as sticky
  var stickyHeaderItem = null;
  var preHeaderSpacerHeight = 0;
  var postHeaderSpacerHeight = 0;

  if (startIndex > 0 && topLevelItems.length > 0 && startIndex <= endIndex) {
    var firstVisibleSection = topLevelItems[startIndex].sectionIndex;
    for (var sh = startIndex - 1; sh >= 0; sh--) {
      if (topLevelItems[sh].type === 'header' && topLevelItems[sh].sectionIndex === firstVisibleSection) {
        stickyHeaderItem = topLevelItems[sh];
        preHeaderSpacerHeight = sh > 0 ? cumHeights[sh - 1] : 0;
        postHeaderSpacerHeight = cumHeights[startIndex - 1] - cumHeights[sh];
        break;
      }
    }
  }

  // rAF-throttled scroll handler: only triggers re-render when visible range changes
  var handleScroll = useCallback(function(e) {
    scrollTopRef.current = e.target.scrollTop;
    if (rafIdRef.current) return; // already scheduled
    rafIdRef.current = requestAnimationFrame(function() {
      rafIdRef.current = null;
      var range = computeRange(scrollTopRef.current);
      setVisibleRange(function(prev) {
        if (prev.startIndex === range.startIndex && prev.endIndex === range.endIndex) {
          return prev; // same range -- skip re-render
        }
        return range;
      });
    });
  }, [setVisibleRange]);

  // Cancel pending rAF on unmount
  useEffect(function() {
    return function() {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // Track previous list identity to avoid spurious scroll resets
  var prevListKeyRef = useRef(null);

  // Measure container on mount and resize
  useEffect(function() {
    var el = scrollRef.current;
    if (!el) return;
    setContainerHeight(el.clientHeight);
    // Build a key from the list content (suuids) to detect real changes
    // vs reference-only changes from parent re-renders
    var listKey = availableLocalList.length + ':' +
      (availableLocalList.length > 0 ? availableLocalList[0].suuid : '') + ':' +
      (availableLocalList.length > 1 ? availableLocalList[availableLocalList.length - 1].suuid : '');
    if (prevListKeyRef.current !== null && prevListKeyRef.current === listKey) {
      return;
    }
    prevListKeyRef.current = listKey;
    // Reset scroll position when field list actually changes
    el.scrollTop = 0;
    scrollTopRef.current = 0;
  }, [availableLocalList, setContainerHeight]);

  useEffect(function() {
    var el = scrollRef.current;
    if (!el) return;
    // Use ResizeObserver if available for dynamic container height
    if (typeof ResizeObserver !== 'undefined') {
      var observer = new ResizeObserver(function(entries) {
        if (entries[0]) {
          setContainerHeight(entries[0].contentRect.height);
        }
      });
      observer.observe(el);
      return function() { observer.disconnect(); };
    }
  }, [setContainerHeight]);

  // Reset scroll when external search query changes
  useEffect(function() {
    if (typeof externalSearchQuery === 'string' && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollTopRef.current = 0;
    }
  }, [externalSearchQuery]);

  var handleSearchChange = function(event) {
    var text = event.target.value;
    setSearchQuery(text);
    // Reset scroll when searching
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollTopRef.current = 0;
    }
  };

  var handleClearSearch = function() {
    setSearchQuery('');
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
      scrollTopRef.current = 0;
    }
  };

  // Preserve scroll position when a drag starts inside the scroll container.
  // Browsers can reset scrollTop during drag ghost creation.
  var handleContainerDragStart = function() {
    var el = scrollRef.current;
    if (!el) return;
    var saved = el.scrollTop;
    requestAnimationFrame(function() {
      if (el.scrollTop !== saved) {
        el.scrollTop = saved;
        scrollTopRef.current = saved;
      }
    });
  };

  // Check if there are any visible fields
  var hasVisibleFields = false;
  for (var vi = 0; vi < topLevelItems.length; vi++) {
    if (topLevelItems[vi].type === 'field') {
      hasVisibleFields = true;
      break;
    }
  }

  var renderSectionHeader = function(item) {
    var headerClassName = 'field-selector-section-header';
    if (item.isFirst) {
      headerClassName += ' field-selector-section-header--first';
    }
    return (
      <div
        key={item.field.suuid}
        className={headerClassName}
        style={{ top: -6, zIndex: 200 }}
      >
        <span className="field-selector-section-header-title">
          {item.field.title}
          {item.field.titleBold && <strong>{item.field.titleBold}</strong>}
        </span>
        {handleBulkAdd && item.sectionFields && item.sectionFields.length > 0 && (
          <button
            type="button"
            className="field-selector-section-bulk-add"
            onClick={(function(sectionItem) {
              return function(e) {
                e.stopPropagation();
                var syntheticField = {
                  title: sectionItem.field.title + (sectionItem.field.titleBold ? ' ' + sectionItem.field.titleBold : ''),
                  children: sectionItem.sectionFields,
                  suuid: sectionItem.field.suuid,
                };
                handleBulkAdd(syntheticField);
              };
            })(item)}
            title="Add all fields"
          >
            <span className="material-symbols-outlined">bolt</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="field-selector-dropdown">
      {!hideSearch && <div className="field-selector-header">
        <i className="material-icons field-selector-search-icon">search</i>
        <TextField
          hintText={searchPlaceholder || 'Search fields...'}
          value={searchQuery}
          onChange={handleSearchChange}
          fullWidth
          underlineShow={false}
          autoFocus
          hintStyle={{
            color: 'var(--sl-field-hint)',
            fontSize: 13,
            bottom: 'auto',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          inputStyle={{
            color: 'var(--sl-text-default)',
            fontSize: 13,
          }}
          style={{
            height: 32,
          }}
        />
        {searchQuery && searchQuery.length >= 1 && (
          <button
            type="button"
            className="field-selector-clear"
            onClick={handleClearSearch}
          >
            <i className="material-icons">close</i>
          </button>
        )}
      </div>}
      <div className="field-selector-content" ref={scrollRef} onScroll={handleScroll} onDragStart={handleContainerDragStart}>
        {topLevelItems.length > 0 && (
          <>
            {stickyHeaderItem ? (
              <>
                {preHeaderSpacerHeight > 0 && <div style={{ height: preHeaderSpacerHeight, flexShrink: 0 }} />}
                {renderSectionHeader(stickyHeaderItem)}
                {postHeaderSpacerHeight > 0 && <div style={{ height: postHeaderSpacerHeight, flexShrink: 0 }} />}
              </>
            ) : (
              topSpacerHeight > 0 && <div style={{ height: topSpacerHeight, flexShrink: 0 }} />
            )}
            {(function() {
              var rendered = [];
              for (var i = startIndex; i <= endIndex; i++) {
                var item = topLevelItems[i];
                if (item.type === 'header') {
                  rendered.push(renderSectionHeader(item));
                } else {
                  rendered.push(
                    <CollapsibleField
                      field={item.field}
                      key={item.field.suuid || item.field.recordEntryAttribute}
                      onFieldClick={onFieldClick}
                      onDragStart={onDragStart}
                      onBulkAdd={handleBulkAdd}
                      disableDrag={disableDrag}
                      useDoubleClick={useDoubleClick}
                      showAddButton={showAddButton}
                      recordsFieldUniquenessIdentifier={recordsFieldUniquenessIdentifier || ''}
                      isLast={item.isLast}
                      stickyTopOffset={SECTION_HEADER_HEIGHT}
                      expandedGroups={expandedGroups}
                      onToggleGroup={handleToggleGroup}
                      visibleSuuids={visibleSuuids}
                    />
                  );
                }
              }
              return rendered;
            })()}
            {bottomSpacerHeight > 0 && <div style={{ height: bottomSpacerHeight, flexShrink: 0 }} />}
          </>
        )}
        {!hasVisibleFields && (
          <div className="field-selector-empty">
            No fields found
          </div>
        )}
      </div>
    </div>
  );
};

RuleFields.propTypes = {
  fields: PropTypes.array.isRequired,
  onFieldClick: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  isRecordListView: PropTypes.bool,
  allFields: PropTypes.array,
  selectedFields: PropTypes.array,
  recordsFieldUniquenessIdentifier: PropTypes.string,
  isColumnPicker: PropTypes.bool,
  recordFieldTitle: PropTypes.string,
  onBulkAdd: PropTypes.func,
  useDoubleClick: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  hideSearch: PropTypes.bool,
  externalSearchQuery: PropTypes.string,
  showAddButton: PropTypes.bool,
  hideEntryDetails: PropTypes.bool,
  hideOtherFields: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(RuleFields);
